import { CaretUpOutlined, ExclamationOutlined } from '@ant-design/icons';
import { Chart } from '@mm/components';
import {
  SEMRushWebsiteTrafficByMonthModel,
  SEMRushWebsiteTrafficModel,
} from '@mm/state/model/SEMRushWebsiteTrafficModel';
import classNames from 'classnames';
import React, { useState } from 'react';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  AVERAGE_WEB_CONVERSION_RATE,
  MAX_LOST_LEADS_THRESHOLD,
  SPLIT_NUMBER_BY_THOUTHANDS,
  AVERAGE_WEB_TRAFFIC_GROWTH,
  WEB_TRAFFIC_GROWTH_WITH_MOBILEMONKEY,
} from '../WebsiteReportSummary/constants';

import styles from './ROIAnalysisPanel.module.css';
import { InputNumber } from 'antd';
import { StatsCard } from './StatCard/StatsCard';
import { LevelMeter } from '../LevelMeter/LevelMeter';
import { TABLET_SMALL } from '../../../../editor/constants/screenSize';
import { useResolutionInfo } from '@mm/hooks';
import { abbreviateNumber } from '@mm/utils';

type Props = {
  website: string;
  isOrganic: boolean;
  traffic: SEMRushWebsiteTrafficModel;
  setTotalTraffic: (value: SEMRushWebsiteTrafficModel) => void;
  trafficByMonth: SEMRushWebsiteTrafficByMonthModel;
  onClick: () => void;
};

// tslint:disable-next-line:cyclomatic-complexity
export const ROIAnalysisPanel = (props: Props) => {
  const {
    website,
    isOrganic,
    traffic,
    trafficByMonth,
    onClick,
    setTotalTraffic,
  } = props;
  const DEFAULT_CONVERSION_RATE = 5;
  const DEFAULT_AVG_ORDER_VALUE = 132;
  const [conversionRate, setConversionRate] = useState(DEFAULT_CONVERSION_RATE);
  const [avgOrderValue, setAvgOrderValue] = useState(DEFAULT_AVG_ORDER_VALUE);
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;

  const trafficByType =
    (isOrganic ? traffic.trafficTypes?.organic : traffic.trafficTypes?.paid) ||
    0;

  const updateTraffic = (inputValue: string | null): void => {
    const newTraffic = { ...traffic };
    if (!newTraffic.trafficTypes) {
      newTraffic.trafficTypes = {};
    }

    if (isOrganic) {
      newTraffic.trafficTypes.organic = parseFloat(
        inputValue || ''.replace(/,/g, ''),
      );
    } else {
      newTraffic.trafficTypes.paid = parseFloat(
        inputValue || ''.replace(/,/g, ''),
      );
    }
    setTotalTraffic(newTraffic);
  };

  const monthlyOrganicTraffic = trafficByMonth.trafficTypes?.organic || [];
  const monthlyPaidTraffic = trafficByMonth.trafficTypes?.paid || [];
  const monthlyTrafficData = isOrganic
    ? monthlyOrganicTraffic
    : monthlyPaidTraffic;
  const conversion = WEB_TRAFFIC_GROWTH_WITH_MOBILEMONKEY;

  const monthlyMissedOrganicLeads = monthlyOrganicTraffic.map(dataItem =>
    Math.ceil(dataItem * conversion),
  );
  const monthlyMissedPaidLeads = monthlyPaidTraffic.map(dataItem =>
    Math.ceil(dataItem * conversion),
  );
  const monthlyMissedLeads = isOrganic
    ? monthlyMissedOrganicLeads
    : monthlyMissedPaidLeads;

  const currentCaptured = Math.floor(
    trafficByType * AVERAGE_WEB_CONVERSION_RATE,
  );
  const additionalCaptured = Math.ceil(
    trafficByType * AVERAGE_WEB_TRAFFIC_GROWTH,
  );
  const currentRevenue = Math.floor(
    currentCaptured * (conversionRate / 100) * avgOrderValue,
  );
  const additionalRevenue = Math.ceil(
    additionalCaptured * (conversionRate / 100) * avgOrderValue,
  );
  const additionalCapturedLocalized =
    additionalCaptured.toLocaleString('en-US');
  const additionalRevenueLocalized = additionalRevenue.toLocaleString('en-US');

  const trafficDataByMonth = trafficByMonth.trafficTypes && {
    labels: trafficByMonth.trafficTypes?.dates || [],
    data: [monthlyMissedLeads, monthlyTrafficData],
    color: ['#D4380D', isOrganic ? '#3B8DBB' : '#7B3BBB'],
    title: ['Lost Leads', 'Traffic'],
    gradientColor: ['#fff4f4', isOrganic ? '#f0fafb' : '#f7f0fb'],
  };

  const tooltipsCallbacks = {
    label: (
      item: { datasetIndex: number; value: number },
      { datasets }: { datasets: { label: string }[] },
    ) => {
      const { label } = datasets[item.datasetIndex];
      const value = item.value
        .toString()
        .replace(SPLIT_NUMBER_BY_THOUTHANDS, ',');

      return `${label}: ${value} ${label === 'Traffic' ? 'visitors' : 'leads'}`;
    },
  };

  return (
    <div className={styles.container}>
      <div className={styles.label}>
        <span className={styles.bold700}>
          {isOrganic || !traffic.trafficTypes?.organic ? 'Part A.' : 'Part B.'}
        </span>{' '}
        {webkitURL} {isOrganic ? 'Organic' : 'Paid'} search traffic lead capture
        report
      </div>

      <div className={classNames(styles.header, styles.bold700)}>
        {isOrganic ? (
          <div className={styles.verticalBlock}>
            <span className={styles.website}>{website}</span>
            Organic search traffic lead capture needs improvement
          </div>
        ) : (
          <div>
            Poor lead capture from{' '}
            <span className={styles.website}>{website}</span> paid search
          </div>
        )}
      </div>

      {(isOrganic && monthlyOrganicTraffic.length) ||
      monthlyPaidTraffic.length ? (
        <div className={styles.chartWrapper}>
          <div className={styles.chartBlock}>
            <Chart
              className={styles.chart}
              name={
                isOrganic
                  ? 'Your organic search traffic over 6 months'
                  : 'Your paid traffic over 6 months'
              }
              headerRightSide={`You have lost ${additionalCapturedLocalized} leads & $${additionalRevenueLocalized} Revenue`}
              subheader={`${trafficByType?.toLocaleString('en-US')} visitors`}
              data={trafficDataByMonth}
              tooltipsCallbacks={tooltipsCallbacks}
              additionalOpts={{
                borderDashXAxis: [0, 10],
                borderDashYAxis: [10, 0],
                stacked: true,
              }}
              ticksCallback={val => abbreviateNumber(val)}
            />
            <div
              className={classNames(styles.explanation, {
                [styles.explanationOrganic]: isOrganic,
              })}
            >
              <span className={styles.website}>{website}</span>{' '}
              <span className={styles.textHighligted}>
                extra {additionalCapturedLocalized}
              </span>{' '}
              leads from organic search are worth an estimated{' '}
              <span className={styles.textHighligted}>
                <CaretUpOutlined />${additionalRevenueLocalized}
              </span>{' '}
              in lost revenue/month
            </div>
          </div>
          <div className={styles.chartBlockRight}>
            <div className={styles.description}>
              <div className={styles.descriptionWithRangeWrapper}>
                <div className={styles.descriptionTexts}>
                  <div className={styles.verticalBlock}>
                    <div className={styles.blockHeading}>
                      Based on your {isOrganic ? 'organic search' : 'paid'}{' '}
                      traffic
                    </div>
                    <div className={styles.boldText}>
                      {trafficByType?.toLocaleString('en-US')} visitors{' '}
                      <span className={styles.smallText}>/month</span>
                    </div>
                  </div>

                  <div className={styles.verticalBlock}>
                    <div className={styles.mediumText}>
                      We estimate you are capturing
                    </div>
                    <div className={styles.boldText}>
                      {currentCaptured.toLocaleString('en-US')} emails{' '}
                      <span className={styles.smallText}>/month</span>
                    </div>
                  </div>
                </div>

                <LevelMeter
                  isVertical={!isMobile}
                  maxThresholdValue={MAX_LOST_LEADS_THRESHOLD}
                  currentValue={additionalCaptured}
                  topLabel={
                    !isOrganic && !isMobile ? 'Room for Improvement' : ''
                  }
                  bottomLabel={
                    isOrganic && !isMobile
                      ? 'Missing 20% of potential leads'
                      : ''
                  }
                  css={{ bottomLabel: styles.levelMeterLabel }}
                />
              </div>
              <div className={styles.card}>
                <div className={styles.cardContent}>
                  <div
                    className={classNames(styles.blockHeading, styles.bold600)}
                  >
                    {' '}
                    With our X-Ray product you can capture
                  </div>
                  <div
                    className={classNames(styles.verticalBlock, styles.number)}
                  >
                    +{additionalCapturedLocalized}
                    <span className={styles.boldText}>emails / month</span>
                  </div>
                </div>
                <PrimaryButton
                  className={styles.primaryButton}
                  onClick={onClick}
                >
                  Capture my missed leads
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className={styles.calculatorBlock}>
        <div className={styles.verticalBlock}>
          <div className={styles.blockHeading}>View and customize </div>
          <div className={styles.header}>
            {isOrganic
              ? 'your ORGANIC SEARCH ROI analysis'
              : 'your PAID ROI analysis'}
          </div>
        </div>

        <div className={styles.calculatorHorizontalBlock}>
          <div className={styles.cardsWrapper}>
            <div className={styles.statsBlockWithNote}>
              <StatsCard
                backgroundColor="#F3F5F7"
                captureRate={2}
                emailsCount={currentCaptured.toLocaleString('en-US')}
                revenue={currentRevenue.toLocaleString('en-US')}
                tag="NOW"
                withXray={false}
              />
              <div className={styles.statNote}>
                <ExclamationOutlined className={styles.exclamation} />
                You are missing ${additionalRevenueLocalized} in monthly revenue
                from {isOrganic ? 'organic' : 'paid'} traffic
              </div>
            </div>
            <div className={styles.statsBlockWithNote}>
              <StatsCard
                backgroundColor="#EBE7FB"
                captureRate={conversion * 100}
                emailsCount={additionalCapturedLocalized}
                revenue={additionalRevenueLocalized}
                tag={<>With X-Ray</>}
                withXray={true}
              />
              <div className={styles.statNoteXRay}>
                This is a calculation based on your traffic and our average
                email capture rate with X-Ray based on your conversion rate and
                average order value.
              </div>
            </div>
          </div>

          <div className={styles.calculator}>
            <span className={styles.bold700}>
              Capturing {conversion * 100}% of your
            </span>{' '}
            <InputNumber
              maxLength={13}
              controls={false}
              value={trafficByType.toLocaleString() || 0}
              onChange={updateTraffic}
              className={classNames(styles.bold600, styles.trafficInput)}
            />{' '}
            <span className={styles.textHighligted}>
              {isOrganic ? 'Organic' : 'Paid'} search traffic
            </span>{' '}
            resulting in an additional{' '}
            <span className={styles.textHighligted}>
              {additionalCapturedLocalized} emails
            </span>{' '}
            and converting{' '}
            <InputNumber
              maxLength={5}
              controls={false}
              value={conversionRate}
              onChange={value =>
                setConversionRate(value || DEFAULT_CONVERSION_RATE)
              }
              addonAfter="%"
              className={classNames(styles.bold600, styles.numberInput)}
            />{' '}
            of them to paid customers with our automation tools at an{' '}
            <span className={styles.textHighligted}>
              average order value of
            </span>{' '}
            <InputNumber
              maxLength={9}
              controls={false}
              value={avgOrderValue}
              onChange={value => setAvgOrderValue(value || 0)}
              prefix="$"
              className={styles.bold600}
            />{' '}
            results in{' '}
            <span className={styles.textHighligted}>
              {' '}
              ${additionalRevenueLocalized} additional revenue.
            </span>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.blockHeading}>
          X-Ray warm leads from {isOrganic ? 'organic' : 'paid'} traffic are
          3-5x more likely to convert
          <div className={styles.mediumText}>
            Use our powerful automation tools to contact and close them
          </div>
        </div>

        <PrimaryButton className={styles.primaryButton} onClick={onClick}>
          Claim my additional ${additionalRevenueLocalized} in lost revenue from{' '}
          {isOrganic ? 'organic' : 'paid'} search
        </PrimaryButton>
      </div>
    </div>
  );
};
